import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Loader2 } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuth } from '../hooks';
import { AuthShell, AuthButton, AuthAlert, authInputClass, authLabelClass } from '../components/AuthShell';

const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: false });

  useEffect(() => {
    if (isAuthenticated) setLoginError(null);
  }, [isAuthenticated]);

  if (isLoading && !loginError && !formData.email && !formData.password) {
    return (
      <div className="flex min-h-screen items-center justify-center text-inkblack-60">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSubmitting) return false;
    setLoginError(null);
    setIsSubmitting(true);
    try {
      const result = await login({
        email: formData.email,
        password: formData.password,
        rememberMe: formData.rememberMe,
      });
      if (!result.success) setLoginError(result.error || 'Invalid email or password');
    } catch (error: any) {
      setLoginError(error.message || 'Network error occurred');
    } finally {
      setIsSubmitting(false);
    }
    return false;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  return (
    <>
      <SEO title="Sign In" description="Access your oddits account to continue" keywords="login, sign in" />

      <AuthShell
        title="Welcome back"
        subtitle="Sign in to your oddits account to continue."
        footer={
          <>
            Don&rsquo;t have an account?{' '}
            <Link to="/auth/register" className="font-semibold text-inkblack no-underline hover:text-inkblack">
              Start free
            </Link>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {loginError && (
            <AuthAlert title="Sign in failed" onClose={() => setLoginError(null)}>
              {loginError}
            </AuthAlert>
          )}

          <div className="space-y-1.5">
            <label htmlFor="email" className={authLabelClass}>Email address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
              <input
                id="email" name="email" type="email" autoComplete="email" required
                value={formData.email} onChange={handleInputChange}
                className={authInputClass} placeholder="you@professional.com"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="password" className={authLabelClass}>Password</label>
              <Link to="/auth/forgot-password" className="text-xs font-medium text-inkblack no-underline hover:text-inkblack">
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
              <input
                id="password" name="password" type={showPassword ? 'text' : 'password'}
                autoComplete="current-password" required
                value={formData.password} onChange={handleInputChange}
                className={authInputClass} placeholder="Enter your password"
              />
              <button
                type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-inkblack-60 hover:text-inkblack-60"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-inkblack-60">
            <input
              id="rememberMe" name="rememberMe" type="checkbox"
              checked={formData.rememberMe} onChange={handleInputChange}
              className="size-4 rounded border-receipt text-inkblack focus:ring-inkblack"
            />
            Remember me
          </label>

          <AuthButton type="submit" loading={isSubmitting}>
            {isSubmitting ? 'Signing in…' : (<>Sign in <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>)}
          </AuthButton>
        </form>
      </AuthShell>
    </>
  );
};

export default LoginPage;
